import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons"

import WizardCard from "../../components/WizardCard"
import OrderedList from "../../components/OrderedList"
import CaptionList from "../../components/CaptionList"
import HelperCard from "../../components/HelperCard"
import { GovtApWizardType } from "@/types/govtAppWizard"
import { govtWizardNextStepsCardLinks } from "./constants"

export interface GovtWizardNextStepsCardProps {
  govtAppWizard: GovtApWizardType | undefined
  isLegalEntity: boolean
  isUnsureOrNone: boolean
}

const GovtWizardNextStepsCard = ({
  govtAppWizard,
  isLegalEntity,
  isUnsureOrNone,
}: GovtWizardNextStepsCardProps) => (
  <WizardCard paddingSize="md-y">
    <div className="flex flex-col gap-6">
      <h2 className="text-3xl font-display font-medium leading-[124%] tracking-[0.6px]">
        Next Steps
      </h2>

      <p className="text-charcoal-500 text-sm font-body leading-[140%] tracking-[0.28px]">
        In the case of government programs, NCX's role is to help you prepare
        everything that will be needed for you to get started. For additional
        assistance personalized to you and your land, you can request a call to
        get connected to an NCX expert who can assist further.
      </p>

      <OrderedList
        list={[
          <>
            <p>
              The first thing you'll need to do is schedule an in-person
              consultation.
              <br />
              We found your local NRCS office. Not yours?{" "}
              <a
                href="https://www.nrcs.usda.gov/contact/find-a-service-center"
                className="link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Find your local NRCS office{" "}
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </a>
            </p>

            {govtAppWizard?.nrcs_office_locations?.length ?? 0 > 0 ? (
              <div className="max-w-[426px] grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-x-12 mt-3 max-w">
                {govtAppWizard?.nrcs_office_locations?.map((office) => (
                  <div key={office["Phone"]} className="flex flex-col gap-0.5">
                    <div>{office["Name"]}</div>

                    <div>{office["Physical Address"]}</div>

                    <div>{office["Contact"]}</div>

                    <div>{office["Email"]}</div>

                    <div>{office["Phone"]}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </>,
          <>
            <CaptionList
              caption="If this is your first visit, bring the following documents to register for a farm number:"
              list={[
                "Proof of identity (driver's license, social security card, IRS EIN number)",
                "Proof of Ownership (copy of recorded deed)",
                "Leases (if any)",
                "Entity Identification Status (articles of incorporation, trust & estate documents, partnership agreement)",
              ]}
              captionClass="!text-charcoal-500 !text-base !font-body font-normal !leading-[130%] !tracking-[0.32px]"
              itemClass="!text-charcoal-500 !text-base !font-body !leading-[130%] !tracking-[0.32px] mt-3"
            />
          </>,
          <>
            <CaptionList
              caption="If not completed in a previous visit, be prepared to complete these forms:"
              list={govtWizardNextStepsCardLinks.filter((link) => {
                if (
                  !isLegalEntity &&
                  link.key === "Member's Information Form"
                ) {
                  return false
                }
                if (!isUnsureOrNone && link.key === "Socially Disadvantaged") {
                  return false
                }
                return true
              })}
              captionClass="!text-charcoal-500 !text-base !font-body font-normal !leading-[130%] !tracking-[0.32px]"
              itemClass="!text-base !font-body !leading-[130%] !tracking-[0.32px] !mb-0 mt-3"
            />
          </>,
          "A land management plan is typically required to receive NRCS cost share funds, so be prepared to share your current management plan or your goals for the property. If you don't have a management plan, NRCS can help with that too.",
          "Once the NRCS verifies your details, the consultation process continues with a site visit that you coordinate directly with the local office.",
        ]}
        itemClass="!text-charcoal-500 !text-base !font-body !leading-[130%] !tracking-[0.32px] last:mb-0"
      />

      <HelperCard className="w-full">
        <p className="text-charcoal-500 text-sm leading-[140%] tracking-[0.28px]">
          NCX can help! Request NCX assistance so that we can identify
          like-minded landowners to conduct the actions needed to achieve your
          goals.
        </p>
      </HelperCard>
    </div>
  </WizardCard>
)

export default GovtWizardNextStepsCard
